import moment from 'moment';
import auth from '@/services/auth.services.js';
import customAlert from '@/utils/customAlert/customAlert';
import store from '@/state/store';

export const debounce = (func, wait = 500) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const toLower = (str = '') => {
  return str.toLowerCase().replaceAll(' ', '-');
};

export function checkIfDuplicateExists(arr) {
  return new Set(arr).size !== arr.length;
}

export const formatMoney = (v) => {
  return `$ ${parseFloat(v || 0).toFixed(2)}`;
};

export const formatPercentString = (v) => {
  let n = `${v}`.replaceAll(',', '.');
  n = parseFloat(n);
  return `${(n * 100).toFixed(2)} %`;
};

export const formatMoneyOnlyNumber = (v) => {
  if (parseFloat(v)) {
    return `$ ${parseFloat(v || 0).toFixed(2)}`;
  }
  return v;
};

export const formatFloatOnlyNumber = (v) => {
  if (parseFloat(v)) {
    return parseFloat(parseFloat(v || 0).toFixed(2));
  }
  return v;
};

export const formatIntOnlyNumber = (v) => {
  if (parseInt(v)) {
    return parseInt(v || 0);
  }

  return v;
};

export function getObjectRaw(objmodel = {}) {
  const strObj = JSON.stringify(objmodel);
  return JSON.parse(strObj);
}

export function getCpfNumbers(cpf = '') {
  return cpf.replaceAll('.', '').replaceAll('-', '');
}

export const formatPercent = (v) => {
  return `${parseFloat(v || 0).toFixed(2)}%`;
};

export const formatFloat = (v) => {
  return parseFloat(parseFloat(v || 0).toFixed(2));
};

export const formatMoneyFloat = (v) => {
  let value = parseFloat(v || 0).toFixed(2);
  return `$ ${value}`.replace('.', ',');
};

export const formatMoneyPositive = (v) => {
  let value = parseFloat(v || 0).toFixed(2);

  if (value < 0) {
    value = 0;
  }

  return `$ ${value}`.replace('.', ',');
};

export const formatFloatPositive = (v) => {
  let value = parseFloat(v || 0).toFixed(2);

  if (value < 0) {
    value = 0;
  }

  return parseFloat(value);
};

export function checkRole(role) {
  const usr = auth.getLoggedUser();
  return (usr?.roles || []).includes(role);
}

export function checkOcultarCpa() {
  if (store.state.companySettings?.ocultar_cpa === true) {
    return true;
  }

  const user = auth.getLoggedUser();
  if (!user?.company?.id) {
    return false;
  }

  const companyId = user.company.id;
  const cached = localStorage.getItem(`company_${companyId}_settings`);

  if (cached) {
    try {
      const settings = JSON.parse(cached);
      if (settings?.ocultar_cpa === true) {
        return true;
      }
    } catch (err) {
      localStorage.removeItem(`company_${companyId}_settings`);
    }
  }

  return user?.company?.settings?.ocultar_cpa === true;
}

function isValidEmail(str = '') {
  for (const c of str) {
    if (!allowedChars.includes(c)) {
      return false;
    }
  }
  return true;
}

function getRandomInt(max) {
  return Math.floor(Math.random() * max);
}

export const formatDateTime = (v) => {
  if (!v) return '';
  return moment.utc(v).format('DD/MM/YYYY HH:mm');
};

/**
 *
 * @param {object} objModel
 * @param {array} orderedKeys
 * @returns Object
 * @example
 * pickOrderObj({name: "Marcos", phone: '(99) 99999-9999', age: 18, gender: "M"},['age','gender','name'])
 * {age: 18, gender: "M", name: 'Marcos'} // output
 */
export function pickOrderObj(objModel = {}, orderedKeys = []) {
  const keys = Object.keys(objModel);
  const fieldsOrdered = [];

  keys.forEach((key) => {
    if (orderedKeys.includes(key)) {
      const index = orderedKeys.findIndex((att) => att === key);
      const field = { [key]: objModel[key] };
      fieldsOrdered.push({ field, index });
    }
  });

  return fieldsOrdered
    .sort((a, b) => a.index - b.index)
    .reduce(
      (obj, item) => ({
        ...obj,
        ...item.field
      }),
      {}
    );
}

function hasOnlyNumbers(str) {
  return /^\d+$/.test(str);
}

const cpfValidation = (inputCPF = '') => {
  var soma = 0;
  var resto;

  if (!inputCPF) {
    return false;
  }

  if (!hasOnlyNumbers(inputCPF)) {
    return false;
  }

  inputCPF = `${inputCPF}`.replaceAll('.', '').replaceAll('-', '');

  if (inputCPF == '00000000000') return false;
  if (!/^\d+$/.test(inputCPF)) return false;
  for (let i = 1; i <= 9; i++) soma = soma + parseInt(inputCPF.substring(i - 1, i)) * (11 - i);
  resto = (soma * 10) % 11;

  if (resto == 10 || resto == 11) resto = 0;
  if (resto != parseInt(inputCPF.substring(9, 10))) return false;

  soma = 0;
  for (let i = 1; i <= 10; i++) soma = soma + parseInt(inputCPF.substring(i - 1, i)) * (12 - i);
  resto = (soma * 10) % 11;

  if (resto == 10 || resto == 11) resto = 0;
  if (resto != parseInt(inputCPF.substring(10, 11))) return false;
  return true;
};

function emailValidation(email) {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}
export function hasSpecialChars(str = '') {
  let specials = /[^A-Za-z 0-9]/g;
  return specials.test(str.replaceAll('-'));
}

export const expressionKeywords = [
  '<ggr>',
  '<turnover>',
  '<admin_fee>',
  '<ngr>',
  '<value>',
  '<gross_value>',
  '<bonus>'
];
export const allowedChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@.-_';
export const partnerCalculation = `(<ggr>-<admin_fee>-<affiliate_reward>-<abatment>)*<percentage>`;

export function expressionValidade(exp = '') {
  expressionKeywords.forEach((key) => {
    exp = exp.replaceAll(key, '(-1)');
  });

  try {
    eval(exp);
  } catch (er) {
    console.log(er);
    return false;
  }

  return true;
}

function validatePassword(password = '') {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+[\]{};':"\\|,.<>/?]/.test(password);

  if (password.length < minLength) {
    customAlert({
      type: 'warning',
      msg: 'The password need 8 or more characters'
    });
    return false;
  }

  if (!hasUpperCase) {
    customAlert({
      type: 'warning',
      msg: 'The password must have an uppercase character'
    });
    return false;
  }

  if (!hasLowerCase) {
    customAlert({
      type: 'warning',
      msg: 'Password must have a lowercase character'
    });
    return false;
  }

  if (!hasNumbers) {
    customAlert({
      type: 'warning',
      msg: 'Password must have a number'
    });
    return false;
  }

  if (!hasSpecialChars) {
    customAlert({
      type: 'warning',
      msg: 'Password must have a special character'
    });
    return false;
  }

  return true;
}

function capitalize(s = '') {
  return s[0].toUpperCase() + s.slice(1);
}

export default {
  capitalize,
  expressionKeywords,
  expressionValidade,
  formatDateTime,
  validatePassword,
  debounce,
  getRandomInt,
  getObjectRaw,
  formatPercent,
  formatFloatOnlyNumber,
  hasOnlyNumbers,
  formatMoneyPositive,
  formatMoneyOnlyNumber,
  formatMoney,
  getCpfNumbers,
  isValidEmail,
  pickOrderObj,
  formatFloat,
  formatIntOnlyNumber,
  checkRole,
  checkOcultarCpa,
  formatFloatPositive,
  checkIfDuplicateExists,
  partnerCalculation,
  formatMoneyFloat,
  cpfValidation,
  emailValidation,
  toLower,
  hasSpecialChars,
  formatPercentString
};
